import React from 'react';
import { Box, Button, CircularProgress } from '@material-ui/core';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';

import { RechartsLinearChart } from 'features/dashboard/components/RechartsLinearChart';
import { Tabs } from 'shared/tabs';
import { InfoChartWrapper } from 'features/dashboard/components/InfoChartWrapper';
import { LinearGraphReducerState, LinearGraphReducerType } from 'features/dashboard/redux/createLinearChartReducer';
import { t } from 'locales/i18n';
import { ChartViewMode } from 'features/dashboard/ts/ChartViewMode';
import { requestLinearGraphData, setLinearGraphMode, shiftLinearGraphData } from 'features/dashboard/redux/actions';
import { pickLinearGraph } from 'features/dashboard/utils/pickGraph';
import { LinearChartItem } from 'features/dashboard/ts/LinearCharItem';
import { useTranslation } from 'hooks/useTranslation';
import { arSA } from 'date-fns/locale';
import { useLanguage } from 'hooks/useLanguage';
import { GridSize } from '@material-ui/core/Grid/Grid';

const chartTabs = [
  { value: 'daily', label: t('daily') },
  { value: 'weekly', label: t('weekly') },
  { value: 'monthly', label: t('monthly') },
];

interface LinearChartProps {
  title: string;
  state: LinearGraphReducerState;
  reducerType: LinearGraphReducerType;
  xs?: GridSize;
  color: string;
}

export const LinearChart: React.FC<LinearChartProps> = ({ title, state, reducerType, xs, color }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('dashboard');
  const { currentLanguageCode } = useLanguage();
  const [previousChartData, setPreviousChartData] = React.useState<LinearChartItem[]>([]);
  const [previousChartMode, setPreviousChartMode] = React.useState<ChartViewMode>(state.mode);

  const handleTabChange = (_: unknown, value: ChartViewMode) => {
    dispatch(setLinearGraphMode(reducerType, value));
  };

  const { isLoading, mode } = state;

  const chartData = pickLinearGraph(state).get(state.rangeAnchor.getTime());

  React.useEffect(() => {
    if (chartData) {
      setPreviousChartData(chartData);
      setPreviousChartMode(mode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);

  React.useEffect(() => {
    dispatch(requestLinearGraphData(reducerType));
  }, [dispatch, reducerType]);

  const xAxisInterval = state.mode === ChartViewMode.Daily ? 3 : 0;

  const createShiftHandler = (direction: 'previous' | 'next') => {
    return () => {
      dispatch(shiftLinearGraphData(reducerType, direction));
    };
  };

  const formatOptions = React.useMemo(
    () => (currentLanguageCode === 'ar' ? { locale: arSA } : {}),
    [currentLanguageCode]
  );

  const formatTickLabel = React.useCallback(
    (value: number) => {
      if (value === 0 || String(value) === 'auto') return '';

      switch (mode) {
        case ChartViewMode.Daily:
          return format(value, 'p', formatOptions);
        case ChartViewMode.Weekly:
          return format(value, 'eee', formatOptions);
        case ChartViewMode.Monthly:
          return format(value, 'dd.MM', formatOptions);
      }
    },
    [formatOptions, mode]
  );

  const formatTooltip = React.useCallback(
    (value: Date) => {
      switch (mode) {
        case ChartViewMode.Daily:
          return format(value, 'dd.MM.yyyy, p', formatOptions);
        case ChartViewMode.Weekly:
          return format(value, 'dd.MM.yyyy', formatOptions);
        case ChartViewMode.Monthly:
          return format(value, 'dd.MM.yyyy', formatOptions);
      }
    },
    [mode, formatOptions]
  );

  const chartModeIsStock = chartData ? mode : previousChartMode;

  return (
    <InfoChartWrapper title={title} xs={xs}>
      <Box display="flex" justifyContent="space-between" paddingBottom={12} paddingTop={4}>
        <Tabs value={chartModeIsStock} tabs={chartTabs} onChange={handleTabChange} />
        <Box display="flex" alignItems="center">
          {isLoading && <CircularProgress size={20} />}
          <Button onClick={createShiftHandler('previous')} disabled={isLoading}>
            {t('linearChart.previous')}
          </Button>
          <Button onClick={createShiftHandler('next')} disabled={isLoading}>
            {t('linearChart.next')}
          </Button>
        </Box>
      </Box>
      <RechartsLinearChart
        color={color}
        data={chartData ?? previousChartData}
        interval={xAxisInterval}
        tickFormatter={formatTickLabel}
        tooltipFormatter={formatTooltip}
      />
    </InfoChartWrapper>
  );
};
